'use client';

import { useLocalStorageState } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';
import { useDoubleFestivalCampaign, useUserNexus } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { Q4_LAUNCH_CAMPAIGN } from '@/app/utils';
import { ANNOUNCEMENTS } from './registry';
import type { AnnouncementConfig, AnnouncementSurface } from './types';

const STORAGE_KEYS: Record<AnnouncementSurface, string> = {
  workspace: 'TOP_ANNOUNCEMENT_CLOSED_IDS',
  landing: 'LANDING_TOP_ANNOUNCEMENT_CLOSED_IDS',
};

export function useTopAnnouncement(onlyId?: string, surface: AnnouncementSurface = 'workspace') {
  const isDoubleFestivalActive = useDoubleFestivalCampaign();
  const { userInfo, subscription, isTeamSpace, countryLevel } = useStore((s) => ({
    userInfo: s.userInfo,
    subscription: s.subscription,
    isTeamSpace: s.isTeamSpace,
    countryLevel: s.countryLevel,
  }));
  const { isSubOneTimeOffer } = useUserNexus();

  const storageKey = onlyId === Q4_LAUNCH_CAMPAIGN.id ? STORAGE_KEYS.workspace : STORAGE_KEYS[surface];
  const [closedIds, setClosedIds] = useLocalStorageState<string[]>(storageKey, {
    defaultValue: [],
    listenStorageChange: true,
  });

  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
  }, []);

  const activeAnnouncement = useMemo<AnnouncementConfig | null>(() => {
    if (now === null) return null;

    const ctx = { userInfo, subscription, isTeamSpace, countryLevel, isSubOneTimeOffer, isDoubleFestivalActive };
    const sorted = [...ANNOUNCEMENTS].sort((a, b) => b.priority - a.priority);

    for (const announcement of sorted) {
      if (onlyId && announcement.id !== onlyId) continue;
      if (announcement.expiresAt && now > announcement.expiresAt.getTime()) continue;
      if (announcement.condition && !announcement.condition(ctx)) continue;
      if (closedIds?.includes(announcement.id)) continue;
      return announcement;
    }

    return null;
  }, [
    now,
    userInfo,
    subscription,
    isTeamSpace,
    countryLevel,
    isSubOneTimeOffer,
    closedIds,
    onlyId,
    isDoubleFestivalActive,
  ]);

  const closeAnnouncement = (id: string) => {
    setClosedIds((prev) => {
      const list = prev ?? [];
      return list.includes(id) ? list : [...list, id];
    });
  };

  return { activeAnnouncement, closeAnnouncement };
}
